import { OdsVendorCard } from '@components/OdsVendorCard/OdsVendorCard';
import type { CardProps } from '@components/trackable_card';
import { MEDIA_QUERY_STATES } from '@constants/media-query';
import { useIsVisible } from '@hooks/useIsVisible';
import { useQueueVendorImpression } from '@hooks/useQueueVendorImpression';
import { useMedia } from '@tkww/orion-web-app-kernel';
import { VendorCard as OrionVendorCard } from '@tkww/orion-web-ds';
import { type ClassesRecord, compose } from '@xo-union/react-css-modules';
import classNames from 'classnames';
import React, { type ReactNode, useMemo } from 'react';
import type { FCWithChildren } from 'types/react-extended';
import OrionVendorCardProps from './helpers/vendor_card_props/OrionVendorCardProps';
import Styles from './styles.scss';

type VendorCardClasses =
	| 'container'
	| 'image-container'
	| 'image'
	| 'label'
	| 'vendor-name'
	| 'review-container'
	| 'stars'
	| 'star-filled'
	| 'star'
	| 'info-container'
	| 'review-count'
	| 'secondary-info-container'
	| 'secondary-info'
	| 'bow-medal'
	| 'featured-label';

interface ComponentProps {
	cardUiLocation?: string | null;
	cardProps: CardProps;
	children?: ReactNode;
	vendorCardClasses?: Partial<ClassesRecord<VendorCardClasses>>;
	handleSave?: (vendor: Vendor.Raw) => void;
	impressionDetails?: VendorImpressions.ImpressionDetails | null;
	isSaved?: boolean;
	onClick?: (event: MouseEvent) => void;
	showHeart?: boolean;
	type?: string;
	vendor: Vendor.Raw;
	newTagEnabled?: boolean;
	aiPhotoEnabled?: boolean;
	renderOdsCard?: boolean;
	centerFavoriteSnackbar?: boolean;
	isDirectoryPageMinPeakPrice?: boolean;
}

const defaultChildren: ReactNode = [];

const defaultProps: Utils.PickOptional<ComponentProps> = {
	cardUiLocation: '',
	vendorCardClasses: {},
	children: defaultChildren,
	handleSave: () => {
		// Do Nothing
	},
	impressionDetails: null,
	isSaved: false,
	onClick: () => {
		// Do Nothing
	},
	showHeart: false,
	type: 'sm',
	newTagEnabled: false,
	aiPhotoEnabled: false,
	renderOdsCard: false,
	centerFavoriteSnackbar: false,
	isDirectoryPageMinPeakPrice: false,
};

export type Props = ComponentProps & typeof defaultProps;

/** @internal */
export const Card: FCWithChildren<Props> = (props) => {
	const {
		vendorCardClasses,
		handleSave,
		impressionDetails,
		isSaved,
		onClick,
		type,
		vendor,
		showHeart,
		children,
		newTagEnabled,
		aiPhotoEnabled,
		renderOdsCard,
		centerFavoriteSnackbar,
		isDirectoryPageMinPeakPrice,
	} = props;

	const queueVendorImpression = useQueueVendorImpression();

	const xxl = useMedia('@xxl');
	const xl = useMedia('@xl');
	const lg = useMedia('@lg');
	const isXxl = xxl === MEDIA_QUERY_STATES.matched;
	const isXl = xl === MEDIA_QUERY_STATES.matched;
	const isLg = lg === MEDIA_QUERY_STATES.matched;
	const screenSizes = [isXxl, isXl, isLg];

	const callback = () => {
		if (impressionDetails) {
			queueVendorImpression(impressionDetails);
		}
	};
	const [targetRef] = useIsVisible({ callback });

	const getOrionVendorCardSize = (): 'sm' | 'md' | 'lg' | undefined => {
		if (type === 'sm' || type === 'md' || type === 'lg') {
			return type;
		}
		return undefined;
	};

	const smallClassName = useMemo(() => {
		if (isXxl) {
			return Styles.smCardXxlWindow;
		}
		if (isXl) {
			return Styles.smCardXlWindow;
		}
		if (isLg) {
			return Styles.smCardLgWindow;
		}
		return undefined;
	}, screenSizes);

	const sectionClassName = useMemo(() => {
		switch (type) {
			case 'sm': {
				const cl = classNames(Styles.sectionContainer, smallClassName);
				return cl;
			}
			case 'wide':
			case 'md':
				return Styles.sectionContainer;
			default:
				return classNames(
					Styles.sectionContainer,
					Styles.unionSectionContainer,
				);
		}
	}, [type, ...screenSizes]);

	const classes = compose(vendorCardClasses);

	return (
		<section ref={targetRef} className={sectionClassName}>
			{renderOdsCard ? (
				<OdsVendorCard
					{...OrionVendorCardProps(
						vendor,
						newTagEnabled,
						aiPhotoEnabled,
						true,
						isDirectoryPageMinPeakPrice,
					)}
					onClick={onClick}
					classes={classes}
					size={getOrionVendorCardSize()}
					actionsOverlay={
						showHeart
							? {
									actionType: 'save',
									onClick: handleSave,
								}
							: undefined
					}
					isFavorite={isSaved}
					centerFavoriteSnackbar={centerFavoriteSnackbar}
					vendorRaw={vendor}
				>
					{children}
				</OdsVendorCard>
			) : (
				<OrionVendorCard
					{...OrionVendorCardProps(
						vendor,
						newTagEnabled,
						aiPhotoEnabled,
						false,
						isDirectoryPageMinPeakPrice,
					)}
					onClick={onClick}
					classes={classes}
					size={getOrionVendorCardSize()}
					actionsOverlay={
						showHeart
							? {
									actionType: 'save',
									onClick: () => handleSave(vendor),
								}
							: undefined
					}
				>
					{children}
				</OrionVendorCard>
			)}
		</section>
	);
};

Card.defaultProps = defaultProps;

export default Card;
